/**
 * OnayContext.js — giriş GEÇİDİNİN durumu (backlog #540, 2026-10-23).
 *
 * Katılımcı ve Dekoratör girişleri, portalı kullanmadan önce yayınlanmış onay metinlerini (KVKK + fuar kuralları) onaylar. Burası
 * GET /onay-metinleri/pending'i okur ve sonucu Routes/OnayKapisi.js'e verir; "ilk giriş" için ayrı bir bayrak yoktur: /pending
 * boşsa hiçbir şey sorulmaz, bir metin yeni sürümle yayınlanınca (#536 adım 4) herkesin /pending'ine yeniden düşer.
 *
 * KİM SORULUR (Deniz, 2026-10-23): firmanın bu fuardaki arayüz setlerinden biri KATILIMCI ya da DEKORATOR olan gerçek firma
 * girişleri (Yerli ve Yabancı). Organizatör girişleri, personel ve personelin firma ÖNİZLEMESİ sorulmaz (önizleme personel adına
 * bir onay kaydı düşürürdü). Ölçüt aktif set DEĞİL setlerden biri: başlıktaki rol değiştirici geçidi açıp kapatmaz.
 * İSTEĞE BAĞLI (isRequired false) metin: gösterilir, atlanabilir; atlama bu giriş oturumu için sessionStorage'da tutulur (sayfa
 * yenilenince yeniden sorulmaz, bir sonraki girişte sorulur). Zorunlu metin atlanamaz.
 *
 * Durumlar (`durum`): kapali (bu giriş sorulmaz ya da portal henüz hazır değil) · yukleniyor (bu giriş için ilk okuma sürüyor) ·
 * hata (okunamadı: geçit KAPALI kalır, "tekrar dene") · bekliyor (sorulacak metin var) · temiz (hiçbir şey kalmadı ya da yalnızca
 * atlanmış isteğe bağlılar).
 * Dil değişince liste yeni dilde yeniden okunur ama eskisi ekranda kalır (durum yukleniyor'a DÖNMEZ: sayfa yeniden kurulmasın).
 * #553 (backend #552): metinler fuara göre değişir (bir fuarın kendi sürümü global olanın önüne geçer, fuar notu eklenir), bu yüzden okuma
 * portalın seçili fuarıyla (`portal.fuarId`) anahtarlanır ve fuar değişince yeniden okunur; fuar `/pending` ve `/accept`e `fuarId` olarak gider.
 * Geçit tıklamayla değil sunucunun yanıtıyla kalkar: `onayla` her metin için POST /accept yazar, ardından /pending'i yeniden
 * okur; geçit yalnızca sunucu boş (ya da yalnızca isteğe bağlı) dediğinde kalkar.
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePortal } from './PortalContext';
import { DIL, getBekleyenOnaylar, onayKaydet } from '../../services/onayMetniService';
import { atlamayiKaydet, atlandiMi, gecitAcikMi, gecitUygulanir } from '../../services/onayGecidi';

const OnayContext = createContext(null);

export const OnayProvider = ({ children }) => {
  const portal = usePortal();
  const { i18n: dil } = useTranslation();
  // PortalProvider'daki gibi (#403): çıkış → başka hesapla giriş aynı sayfada token'ı değiştirir, konum değişimi bunu okutur.
  useLocation();
  const token = localStorage.getItem('token');
  const dilId = dil?.language === 'en' ? DIL.EN : DIL.TR;
  const fuarId = portal?.fuarId ?? null;
  const uygulanir = gecitUygulanir(portal) && Boolean(token);

  // Son BİTEN okuma: { token, dilId, fuarId, tetik, bekleyenler, hata } (hangi giriş, dil, fuar ve "tekrar dene" sayacı için okundu).
  // Başka bir token'ınki sayılmaz.
  const [okuma, setOkuma] = useState(null);
  const [tetik, setTetik] = useState(0);
  // Atlama bellekte de tutulur: sessionStorage yazılamazsa (gizli pencere) düğme yine bu yükleme için işler.
  const [atlananToken, setAtlananToken] = useState(null);

  // Şu an GEÇERLİ okumanın anahtarı (token | dil | fuar | sayaç). Bir okuma yalnızca bitişinde hâlâ bu anahtarsa yazılır: dil ya da hesap
  // değişmişse ya da onay sonrası yeni okuma başlamışsa eskisi atılır. Effect temizliğiyle iptal EDİLMEZ: portal bağlamı açılışta iki
  // kez yüklenir (fuarsız, sonra fuarlı) ve `uygulanir` kısa süre düşüp döner; okuma tam o aralıkta biterse sonucu çöpe gitmesin.
  const guncelAnahtar = useRef(null);

  // KURAL: fetch Effect içindeki async fonksiyonda (react-hooks/set-state-in-effect).
  useEffect(() => {
    if (!uygulanir) return;
    // Bu giriş, dil, fuar ve sayaç için zaten okunmuş (başarılı ya da başarısız): yeniden okunmaz. Başarısız bir okuma da "okunmuş" sayılır,
    // yoksa sürekli yeniden denerdi: yeniden deneme `yenile` ile (sayaç) olur.
    if (okuma && okuma.token === token && okuma.dilId === dilId && okuma.fuarId === fuarId && okuma.tetik === tetik) return;
    const anahtar = `${token}|${dilId}|${fuarId}|${tetik}`;
    guncelAnahtar.current = anahtar;
    async function run() {
      try {
        const bekleyenler = await getBekleyenOnaylar({ languageId: dilId, fuarId });
        if (guncelAnahtar.current === anahtar) setOkuma({ token, dilId, fuarId, tetik, bekleyenler, hata: null });
      } catch (e) {
        // Bu giriş için elde zaten bir liste varsa (dil / fuar değişimi, yenileme) o kalır (yeni dil / fuar / sayaçla işaretlenir); yoksa geçit hata gösterir.
        if (guncelAnahtar.current === anahtar) {
          setOkuma((onceki) => (onceki?.token === token && !onceki.hata
            ? { ...onceki, dilId, fuarId, tetik }
            : { token, dilId, fuarId, tetik, bekleyenler: [], hata: e.message }));
        }
      }
    }
    run();
  }, [uygulanir, token, dilId, fuarId, tetik, okuma]);

  const bu = okuma?.token === token ? okuma : null;
  const bekleyenler = useMemo(() => bu?.bekleyenler ?? [], [bu]);
  const atlandi = uygulanir && (atlananToken === token || atlandiMi(token));

  let durum = 'kapali';
  if (uygulanir) {
    if (!bu) durum = 'yukleniyor';
    else if (bu.hata) durum = 'hata';
    else durum = gecitAcikMi(bekleyenler, atlandi) ? 'bekliyor' : 'temiz';
  }

  const yenile = useCallback(() => setTetik((n) => n + 1), []);

  /**
   * Bir pencerenin metinlerini sırayla onaylar, sonra /pending'i yeniden okur. Bir POST hata verirse durur ve hatayı fırlatır
   * (öncekiler yazılmıştır; tekrar etkisizdir). Liste yenilenemezse de fırlatır: geçit yanlışlıkla kalkmaz.
   */
  const onayla = useCallback(async (metinler) => {
    guncelAnahtar.current = null; // süren eski bir okuma, onaydan önceki listeyle bu okumanın üstüne yazmasın
    for (const m of metinler) await onayKaydet(m.anahtar, { languageId: m.languageId, fuarId });
    // taze: onaydan önce başlamış bir okumaya katılmaz (onaylananları hâlâ bekliyor gösterirdi)
    const bekleyenler = await getBekleyenOnaylar({ languageId: dilId, fuarId, taze: true });
    setOkuma({ token, dilId, fuarId, tetik, bekleyenler, hata: null });
  }, [dilId, fuarId, token, tetik]);

  /** Kalan isteğe bağlı metinleri bu giriş için atlar. Zorunlu metin kaldıysa hiçbir şey yapmaz. */
  const atla = useCallback(() => {
    if (bekleyenler.some((m) => m.zorunlu !== false)) return;
    atlamayiKaydet(token);
    setAtlananToken(token);
  }, [bekleyenler, token]);

  const deger = useMemo(
    () => ({ durum, bekleyenler, hata: bu?.hata ?? null, dilId, onayla, atla, yenile }),
    [durum, bekleyenler, bu?.hata, dilId, onayla, atla, yenile],
  );

  return <OnayContext.Provider value={deger}>{children}</OnayContext.Provider>;
};

/** Geçit durumu; OnayProvider'ın dışında null (testler). */
export const useOnay = () => useContext(OnayContext);

export default OnayContext;
